import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Single axios instance for authed calls with in-memory access token
export const api = axios.create({ baseURL: API, withCredentials: true });

let _accessToken = null;
const setAccessToken = (t) => { _accessToken = t || null; };
export const getAccessToken = () => _accessToken;

api.interceptors.request.use((config) => {
    const imp = getImpersonation();
    if (imp?.token) config.headers.Authorization = `Bearer ${imp.token}`;
    else if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;
    return config;
});

// ---- Impersonation (Super Admin troubleshooting) ----
export const getImpersonation = () => {
    try { return JSON.parse(sessionStorage.getItem("noxn.imp") || "null"); } catch (e) { return null; }
};
export const startImpersonation = (token, user) => {
    sessionStorage.setItem("noxn.imp", JSON.stringify({ token, user }));
    window.location.href = user.role === "client" ? "/account" : "/dashboard";
};
export const stopImpersonation = () => {
    sessionStorage.removeItem("noxn.imp");
    window.location.href = "/dashboard/admin";
};

const AuthCtx = createContext(null);
let refreshInFlight = null;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const refreshTimer = useRef(null);

    const scheduleRefresh = useCallback(() => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        // access token = 15 min; refresh at 12 min
        refreshTimer.current = setTimeout(() => { refresh().catch(() => {}); }, 12 * 60 * 1000);
    }, []);

    const refresh = useCallback(async () => {
        // Single-flight: concurrent callers (StrictMode double-mount, parallel tabs)
        // share one request so the rotating refresh cookie is only used once.
        if (!refreshInFlight) {
            refreshInFlight = api.post("/auth/refresh").finally(() => { refreshInFlight = null; });
        }
        try {
            const { data } = await refreshInFlight;
            setAccessToken(data.access_token);
            setUser(data.user);
            scheduleRefresh();
            return data.user;
        } catch (e) {
            setAccessToken(null);
            setUser(null);
            return null;
        }
    }, [scheduleRefresh]);

    useEffect(() => {
        const imp = getImpersonation();
        if (imp?.user) { setUser(imp.user); setLoading(false); return; }
        (async () => { await refresh(); setLoading(false); })();
        return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
    }, [refresh]);

    const requestCode = useCallback(async (contact) => {
        const { data } = await api.post("/auth/request-code", { contact });
        return data;
    }, []);

    const verifyCode = useCallback(async (contact, code, remember_me = false) => {
        const { data } = await api.post("/auth/verify-code", { contact, code, remember_me });
        setAccessToken(data.access_token);
        setUser(data.user);
        scheduleRefresh();
        return data.user;
    }, [scheduleRefresh]);

    const registerAccount = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        return data;
    }, []);

    const loginStart = useCallback(async (email, device_token) => {
        const { data } = await api.post("/auth/login-start", { email, device_token: device_token || null });
        return data;
    }, []);

    const loginPassword = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login-password", { email, password });
        return data;
    }, []);

    const trustDevice = useCallback(async () => {
        const { data } = await api.post("/auth/trust-device");
        return data.device_token;
    }, []);

    const setPassword = useCallback(async (password, current_password) => {
        const { data } = await api.post("/auth/set-password", { password, current_password: current_password || null });
        return data;
    }, []);

    const updateTheme = useCallback(async (theme_preference) => {
        try {
            const { data } = await api.patch("/auth/me/theme", { theme_preference });
            setUser(data);
            return data;
        } catch (e) {
            return null;
        }
    }, []);

    const updateTimezone = useCallback(async (timezone) => {
        const { data } = await api.patch("/auth/me/timezone", { timezone });
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        setAccessToken(null);
        setUser(null);
    }, []);

    const value = useMemo(() => ({
        user, loading, requestCode, verifyCode, updateTheme, updateTimezone, logout, refresh,
        registerAccount, loginStart, loginPassword, trustDevice, setPassword,
    }), [user, loading, requestCode, verifyCode, updateTheme, updateTimezone, logout, refresh,
        registerAccount, loginStart, loginPassword, trustDevice, setPassword]);
    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

// Trusted-device tokens (per email) — lets returning users skip the password step.
const DEVICE_KEY = "noxn.trustedDevices";
export const getDeviceToken = (email) => {
    try {
        const map = JSON.parse(localStorage.getItem(DEVICE_KEY) || "{}");
        return map[(email || "").toLowerCase()] || null;
    } catch (e) { return null; }
};
export const storeDeviceToken = (email, token) => {
    try {
        const map = JSON.parse(localStorage.getItem(DEVICE_KEY) || "{}");
        map[(email || "").toLowerCase()] = token;
        localStorage.setItem(DEVICE_KEY, JSON.stringify(map));
    } catch (e) { /* ignore */ }
};

export const useAuth = () => {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
